import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useTheme } from "@/lib/theme";

const COOKIE_NAME = "prid_gdpr";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180; // 180 days

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

function writeCookie(name, value) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

/**
 * Compact GDPR notice. Anchored bottom-right on desktop, slim bar on mobile.
 * Copy is short and non-intrusive because we already do the right thing:
 * one first-party cookie, no trackers.
 */
export default function GdprBanner() {
  const [visible, setVisible] = useState(false);
  const { theme } = useTheme();
  const dark = theme === "dark";

  useEffect(() => {
    if (!readCookie(COOKIE_NAME)) {
      // Small delay so it doesn't appear before the hero paints
      const id = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(id);
    }
  }, []);

  function decide(value) {
    writeCookie(COOKIE_NAME, value);
    setVisible(false);
  }

  if (!visible) return null;

  const surface = dark
    ? "bg-[#141416] border-white/15 text-slate-100"
    : "bg-white border-slate-200 text-slate-900";
  const subtle = dark ? "text-slate-400" : "text-slate-500";
  const linkColor = dark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900";

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-labelledby="gdpr-title"
      data-testid="gdpr-banner"
      className={`fixed z-50 no-print bottom-4 left-4 right-4 md:left-auto md:bottom-6 md:right-6 md:w-[380px] border ${surface} shadow-[8px_8px_0_0_rgba(255,197,61,0.35)] animate-in slide-in-from-bottom-2 fade-in duration-300`}
    >
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="gdpr-dismiss"
        onClick={() => decide("decline")}
        className={`absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-sm ${linkColor} transition-colors hover:bg-slate-100 dark:hover:bg-white/5`}
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="p-5 pr-10">
        <div className="flex items-center gap-2 mb-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#FFC53D]" />
          <p id="gdpr-title" className={`text-[10px] tracking-[0.28em] uppercase font-semibold ${subtle}`}>
            Cookies · GDPR
          </p>
        </div>

        <p className="text-[13px] leading-relaxed">
          One first-party cookie remembers your language and currency. No trackers, no third-party analytics — ever.
        </p>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            data-testid="gdpr-accept"
            onClick={() => decide("accept")}
            className="px-4 py-2 bg-slate-900 text-white text-xs uppercase tracking-[0.18em] font-semibold rounded-sm hover:bg-slate-800 transition-colors"
          >
            Got it
          </button>
          <Link
            to="/privacy"
            data-testid="gdpr-privacy-link"
            className={`text-[11px] tracking-wide underline underline-offset-4 decoration-slate-300 transition-colors ${linkColor}`}
          >
            Privacy policy
          </Link>
        </div>
      </div>
    </div>
  );
}
